/* eslint-disable react/jsx-no-useless-fragment */
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { cn, useResponsiveBreakPoints, CircleMinusIcon } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { Variable } from '@restworld/utility-types';
import { ClipboardDocumentListIcon, ShareIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
// import { useRecordJobShareEvent } from '../../hooks/use-record-job-share-event';
import OtherRequirements from '../job-other-requirements';
import { Heading } from '@restworld/ui-ds-base';
import { Wrapper } from '@restworld/ui-components';
import { SecondaryList } from '../legacy/list-secondary';
import { ListModal } from '../legacy/modal-list';
import { JobShareModal } from '../legacy';

export const SkillsBlock = memo(
  ({
    requiredSkills = [],
    optionalSkills,
    otherRequirements,
    education,
    certificates,
    languages,
    workerSkills,
    children: applicationEntryPointButtonAsChild,
    ambassadorRefCode,
    jobPositionId,
  }: SkillsBlockProps) => {
    const { isDesktop } = useResponsiveBreakPoints();
    const { t } = useTranslation('job-position-page');
    // const { recordJobShareEvent } = useRecordJobShareEvent();
    return (
      <Wrapper>
        <span className="flex flex-row items-center gap-1.5">
          <ClipboardDocumentListIcon className="h-5" />
          <Heading>{t('job_skills.title')}</Heading>
        </span>
        <NecessarySkills requiredSkills={[...requiredSkills]} workerSkills={workerSkills} />
        <OptionalSkills optionalSkills={optionalSkills} />
        <OtherRequirementsEl
          otherRequirements={otherRequirements}
          education={education}
          certificates={certificates}
          languages={languages}
        />
        {!isDesktop ? (
          <ButtonWrapper>
            <JobShareModal
              ambassadorRefCode={ambassadorRefCode}
              jobPositionId={jobPositionId}
              buttonTitle="Condividi"
              StartIcon={ShareIcon}
              fullwidth
              // onClick={recordJobShareEvent}
            />
            {applicationEntryPointButtonAsChild}
          </ButtonWrapper>
        ) : null}
      </Wrapper>
    );
  }
);
export default SkillsBlock;
const NecessarySkills = ({ requiredSkills, workerSkills }: NecessarySkillProps) => {
  const { isAuthenticated } = useAuth();
  const filterCount = useListFilterCount();
  const renderListEl = (showFullList: boolean) =>
    requiredSkills?.length ? (
      <RequiredSkillsList
        options={[...requiredSkills].filter((_, i) => (!showFullList ? i < filterCount : true))}
        value={isAuthenticated ? workerSkills : []}
        isWrapList={!showFullList}
      />
    ) : null;
  return (
    <ListModal isShowMoreButtonVisible={requiredSkills?.length > filterCount} modalEl={renderListEl(true)}>
      {renderListEl(false)}
    </ListModal>
  );
};
// Required skills are a read-only display, not an input — so we don't use CheckboxGroup here
// (the new ui-ds-base Checkbox renders a square input, which reads as interactive). This mirrors
// master's look: a circle-minus (skill the worker still lacks) or a filled check (skill they have).
const RequiredSkillsList = ({
  options,
  value,
  isWrapList,
}: {
  options: Variable[];
  value: string[] | number[];
  isWrapList?: boolean;
}) => (
  // Vertical list (one skill per row). Long collapsed lists wrap into a second column via
  // max-height + flex-wrap (matches master), but short lists stay a single vertical column.
  <ul className={cn('flex flex-col gap-2 w-full', isWrapList && 'md:max-h-[15rem] md:flex-wrap md:gap-x-8')}>
    {options.map(({ key, label, id }) => {
      const workerHasSkill = Array.isArray(value) && value.indexOf(id as never) !== -1;
      return (
        <li key={key} className="flex items-center gap-2">
          {workerHasSkill ? (
            <CheckCircleIcon className="h-4 w-4 min-h-[16px] min-w-[16px] text-purple-700" />
          ) : (
            <CircleMinusIcon className="h-4 w-4 min-h-[16px] min-w-[16px]" fill="#c4b1e7" />
          )}
          <span className="text-base text-gray-600">{label}</span>
        </li>
      );
    })}
  </ul>
);
const OptionalSkills = ({ optionalSkills }: { optionalSkills: string[] }) => {
  const { t } = useTranslation('job-position-page');
  const isEmptyOptionalSkills = Boolean(!optionalSkills?.length);
  const filterCount = useListFilterCount();
  const getOptionalList = useCallback(
    (showFullList: boolean) =>
      !isEmptyOptionalSkills ? (
        <>
          <SecondaryList
            itemList={optionalSkills
              ?.filter((_, i) => (!showFullList ? i < filterCount : true))
              .map((v) => ({ key: v, label: v, icon: 'ClipboardDocumentCheckIcon' }))}
            orientation="vertical"
            textColor="gray"
            isWrapList={!showFullList}
            fontSize="base"
          />
        </>
      ) : null,
    [filterCount, isEmptyOptionalSkills, optionalSkills]
  );

  const optionalEl = optionalSkills?.length ? (
    <ListModal isShowMoreButtonVisible={optionalSkills?.length > filterCount} modalEl={getOptionalList(true)}>
      <Heading size={5}>{t('job_optional_skills_title')}</Heading>
      {getOptionalList(false)}
    </ListModal>
  ) : null;
  return optionalEl;
};
const OtherRequirementsEl = ({ otherRequirements, education, certificates, languages }: OtherRequirementsProps) => {
  const isEmptyOtherRequirements = useMemo(
    () => Boolean(!otherRequirements?.length && !education?.length && !certificates?.length && !languages?.length),
    [certificates?.length, education?.length, languages?.length, otherRequirements?.length]
  );
  return !isEmptyOtherRequirements ? (
    <OtherRequirements
      otherRequirements={otherRequirements}
      education={education}
      certificates={certificates}
      languages={languages}
    />
  ) : null;
};
const useListFilterCount = () => {
  const { isDesktop } = useResponsiveBreakPoints();
  return isDesktop ? 12 : 5;
};
const ButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex gap-4 text-lg justify-center items-center lg:w-full lg:max-w-[21rem] lg:flex-row-reverse',
      className
    )}
    {...props}
  />
);

export interface NecessarySkillProps {
  requiredSkills: Variable[];
  workerSkills: number[];
}
export interface OtherRequirementsProps {
  otherRequirements: string[];
  education: string[];
  certificates: string[];
  languages: {
    key: string;
    label: string;
    level: string;
  }[];
}
export interface SkillsBlockProps extends NecessarySkillProps, OtherRequirementsProps {
  optionalSkills: string[];
  compatibility_score?: number;
  children?: React.ReactNode; // applicationEntryPointButtonAsChild
  ambassadorRefCode?: string;
  jobPositionId?: string;
}
